import { useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { formatDepot } from './depot-format';
import { Input } from '@/components/ui/input';
import { DepotRef } from '@/types';
import { X } from 'lucide-react';

interface Props {
  selected: DepotRef[];
  options: DepotRef[];
  disabled?: boolean;
  placeholder: string;
  emptyLabel: string;
  noMatchLabel: string;
  onChange: (depots: DepotRef[]) => void;
}

/**
 * Autosuggest input for depot references, rendered as removable chips + a
 * type-to-filter text field with a suggestion list. Faster than a dropdown when
 * many depots must be added (e.g. large central events) and partners already
 * know their depot keys. Enter adds the top suggestion; click adds a specific one.
 */
export function DepotAutosuggest({
  selected,
  options,
  disabled = false,
  placeholder,
  emptyLabel,
  noMatchLabel,
  onChange,
}: Props) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedKeys = useMemo(() => new Set(selected.map((d) => d.key)), [selected]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((o) => !selectedKeys.has(o.key))
      // Matches the name as well, so "Köln" finds D004 without knowing its number.
      .filter((o) => q === '' || `${o.key} ${o.name ?? ''}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [options, selectedKeys, query]);

  const add = (d: DepotRef) => {
    if (selectedKeys.has(d.key)) return;
    onChange([...selected, d]);
    setQuery('');
    inputRef.current?.focus();
  };
  const remove = (key: string) => onChange(selected.filter((d) => d.key !== key));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) add(suggestions[0]);
    } else if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      remove(selected[selected.length - 1].key);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[1.5rem]">
        {selected.length === 0 ? (
          <span className="text-sm text-muted-foreground">{emptyLabel}</span>
        ) : (
          selected.map((d) => (
            <Badge key={d.key} variant="secondary" className="gap-1" title={formatDepot(d)}>
              {/* Number monospaced so a column of chips lines up; the name reads
                  as secondary text next to it. */}
              <span className="font-mono">{d.key}</span>
              {d.name && <span className="font-normal">{d.name}</span>}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(d.key)}
                  className="ml-0.5 rounded-sm hover:bg-muted-foreground/20"
                  aria-label={`${d.key} entfernen`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))
        )}
      </div>
      {!disabled && (
        <div className="relative">
          <Input
            ref={inputRef}
            value={query}
            placeholder={placeholder}
            className="w-[260px]"
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={handleKeyDown}
          />
          {focused && (query.trim() !== '' || suggestions.length > 0) && (
            <div className="absolute z-50 mt-1 w-[260px] rounded-md border bg-popover shadow-md">
              {suggestions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">{noMatchLabel}</div>
              ) : (
                <ul className="max-h-56 overflow-auto py-1">
                  {suggestions.map((d) => (
                    <li key={d.key}>
                      <button
                        type="button"
                        className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => add(d)}
                      >
                        {/* Number stays monospaced so the column lines up; the
                            name is secondary and may wrap away on narrow lists. */}
                        <span className="font-mono">{d.key}</span>
                        {d.name && (
                          <span className="truncate text-muted-foreground">{d.name}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
